/**
 * adaptiveGovernance.ts — MER Algo Footprints v8.5.2
 *
 * Adaptive Governance & Control Layer.
 *
 * ── Responsibility ────────────────────────────────────────────────────────────
 * Provides stateful governance over adaptive learning changes:
 *   - AdaptationLedger   — append-only audit log of every proposed/applied change
 *   - GovernanceState    — centralized mode, caps, freeze, approval settings
 *   - ProposalSystem     — approve/reject modifier proposals before application
 *   - RollbackEngine     — revert last, revert by signal, revert all
 *   - LearningHealthMonitor — drift detection, auto-freeze on instability
 *
 * ── Safety invariants (non-negotiable) ───────────────────────────────────────
 * 1. No modifier applies without a ledger entry — ZERO silent changes
 * 2. Freeze mode blocks ALL adaptation immediately, no rebuild required
 * 3. All rollbacks produce a compensating ledger entry (audit trail preserved)
 * 4. Max modifier jump per step is capped at GOVERNANCE_THRESHOLDS.MAX_JUMP
 * 5. Minimum sample enforcement re-checked at governance layer (defense-in-depth)
 * 6. Replay compatibility: full ledger is serializable; state is reconstructable
 *
 * ── State model ───────────────────────────────────────────────────────────────
 * GovernanceStore is a module-level singleton.
 * All mutations go through the exported action functions.
 * Consumers get snapshots via getGovernanceSnapshot() — pure, serializable.
 *
 * ── Replay safety ────────────────────────────────────────────────────────────
 * The full ledger + approved proposals are serializable.
 * Replaying from a serialized snapshot restores identical governance state.
 * Ledger entries carry a wall-clock `recordedAt` (NOT part of deterministic output)
 * for debugging only — marked clearly as non-semantic.
 */

import type {
  RegimeKey,
  AdaptiveLearningProfile,
} from './adaptiveLearning';
import { ADAPTIVE_LEARNING_THRESHOLDS as T } from './adaptiveLearning';
import type { AdaptiveAuditEntry } from './adaptiveLearning';

// ── Governance thresholds ─────────────────────────────────────────────────────

export const GOVERNANCE_THRESHOLDS = {
  // Maximum modifier change in a single step (prevents runaway adaptation)
  MAX_JUMP:                   0.05,    // e.g. 1.00 → 1.05 max per step

  // Modifier volatility: if std dev of last N entries exceeds this, trigger warning
  VOLATILITY_WINDOW:          5,       // last 5 ledger entries for volatility calc
  VOLATILITY_FREEZE_THRESHOLD: 0.08,   // std dev > 0.08 = auto-freeze

  // Reliability trend: how many consecutive drops before drift alert
  RELIABILITY_DROP_CONSECUTIVE: 3,

  // Maximum pending proposals before UI warning
  MAX_PENDING_PROPOSALS:       10,

  // Minimum time between active adaptations per signal (ms) — prevents thrashing
  MIN_ADAPTATION_INTERVAL_MS:  0,      // 0 = no limit (pure determinism from data)

  // Auto-approve threshold: if modifier change is tiny, auto-approve
  AUTO_APPROVE_MAX_DELTA:      0.01,
} as const;

const G = GOVERNANCE_THRESHOLDS;

// ── Types ─────────────────────────────────────────────────────────────────────

export type ProposalStatus = 'pending' | 'approved' | 'rejected' | 'applied' | 'rolled_back';
export type GovernanceMode  = 'frozen' | 'advisory' | 'active';
export type HealthStatus    = 'healthy' | 'warning' | 'frozen';

export interface LedgerEntry {
  /** Monotonically increasing sequence number — deterministic ordering */
  seq:              number;
  signalKey:        string;
  previousModifier: number;
  newModifier:      number;
  regimeContext:    RegimeKey;
  reason:           string;
  sampleSize:       number;
  mode:             GovernanceMode;
  /** Whether the modifier was actually applied (false = advisory/rejected) */
  applied:          boolean;
  proposalId:       string;
  /** Wall-clock timestamp — NON-SEMANTIC, for debugging/display only */
  recordedAt:       number;
}

export interface ModifierProposal {
  id:               string;
  signalKey:        string;
  currentModifier:  number;
  proposedModifier: number;
  delta:            number;      // proposedModifier - currentModifier
  regimeContext:    RegimeKey;
  reason:           string;
  sampleSize:       number;
  status:           ProposalStatus;
  createdSeq:       number;      // ledger seq at proposal creation
  resolvedSeq:      number | null;
}

export interface LearningHealthSnapshot {
  status:              HealthStatus;
  modifierVolatility:  number | null;   // std dev of recent modifier changes
  reliabilityTrend:    'improving' | 'stable' | 'degrading' | 'insufficient_data';
  consecutiveDrops:    number;
  frozenReason:        string | null;
  warnings:            string[];
}

export interface GovernanceSnapshot {
  mode:             GovernanceMode;
  frozenReason:     string | null;
  requireApproval:  boolean;
  modifierCap:      { min: number; max: number };
  currentModifiers: Record<string, number>;
  ledger:           LedgerEntry[];
  proposals:        ModifierProposal[];
  health:           LearningHealthSnapshot;
  stats: {
    totalProposals:   number;
    approvedCount:    number;
    rejectedCount:    number;
    rolledBackCount:  number;
    appliedCount:     number;
  };
}

// ── Internal governance store ─────────────────────────────────────────────────

interface GovernanceStore {
  mode:             GovernanceMode;
  frozenReason:     string | null;
  requireApproval:  boolean;
  currentModifiers: Record<string, number>;
  ledger:           LedgerEntry[];
  proposals:        ModifierProposal[];
  nextSeq:          number;
  nextProposalId:   number;
}

function _makeStore(): GovernanceStore {
  return {
    mode:             'advisory',
    frozenReason:     null,
    requireApproval:  true,
    currentModifiers: {},
    ledger:           [],
    proposals:        [],
    nextSeq:          1,
    nextProposalId:   1,
  };
}

// Module-level singleton
let _store: GovernanceStore = _makeStore();

// ── Health monitor ────────────────────────────────────────────────────────────

function _computeHealth(): LearningHealthSnapshot {
  const recentEntries = _store.ledger
    .filter(e => e.applied)
    .slice(-G.VOLATILITY_WINDOW);

  let volatility: number | null = null;
  const warnings: string[] = [];

  if (recentEntries.length >= 2) {
    const deltas = recentEntries.map(e => e.newModifier - e.previousModifier);
    const mean   = deltas.reduce((s, d) => s + d, 0) / deltas.length;
    const variance = deltas.reduce((s, d) => s + (d - mean) ** 2, 0) / deltas.length;
    volatility = Math.round(Math.sqrt(variance) * 1000) / 1000;

    if (volatility > G.VOLATILITY_FREEZE_THRESHOLD) {
      warnings.push(`Modifier volatility (${volatility.toFixed(3)}) exceeds freeze threshold (${G.VOLATILITY_FREEZE_THRESHOLD})`);
    }
  }

  // Reliability trend: consecutive entries where newModifier < previousModifier
  let consecutiveDrops = 0;
  for (let i = _store.ledger.length - 1; i >= 0 && consecutiveDrops < G.RELIABILITY_DROP_CONSECUTIVE + 1; i--) {
    if (_store.ledger[i].applied && _store.ledger[i].newModifier < _store.ledger[i].previousModifier) {
      consecutiveDrops++;
    } else break;
  }

  const reliabilityTrend = recentEntries.length < 2 ? 'insufficient_data'
    : consecutiveDrops >= G.RELIABILITY_DROP_CONSECUTIVE ? 'degrading'
    : recentEntries.some(e => e.newModifier > e.previousModifier) ? 'improving'
    : 'stable';

  if (consecutiveDrops >= G.RELIABILITY_DROP_CONSECUTIVE) {
    warnings.push(`${consecutiveDrops} consecutive reliability drops detected`);
  }
  if (_store.proposals.filter(p => p.status === 'pending').length >= G.MAX_PENDING_PROPOSALS) {
    warnings.push(`${_store.proposals.filter(p => p.status === 'pending').length} proposals pending review`);
  }

  // Auto-freeze on high volatility
  if (volatility !== null && volatility > G.VOLATILITY_FREEZE_THRESHOLD && _store.mode === 'active') {
    _store.mode = 'frozen';
    _store.frozenReason = `Auto-frozen: modifier volatility ${volatility.toFixed(3)} exceeded threshold`;
  }

  const status: HealthStatus = _store.mode === 'frozen' ? 'frozen'
    : warnings.length > 0 ? 'warning' : 'healthy';

  return {
    status, modifierVolatility: volatility, reliabilityTrend,
    consecutiveDrops, frozenReason: _store.frozenReason, warnings,
  };
}

// ── Proposal ID generation ────────────────────────────────────────────────────

function _nextPropId(): string {
  return `PROP-${String(_store.nextProposalId++).padStart(4, '0')}`;
}

// ── Safety validation ─────────────────────────────────────────────────────────

interface SafetyCheck {
  safe:   boolean;
  reason: string | null;
}

function _validateProposal(
  signalKey:         string,
  proposedModifier:  number,
  sampleSize:        number,
): SafetyCheck {
  if (_store.mode === 'frozen') {
    return { safe: false, reason: `Governance frozen: ${_store.frozenReason}` };
  }
  if (sampleSize < T.MIN_SAMPLE_SIZE) {
    return { safe: false, reason: `Insufficient samples: ${sampleSize} < ${T.MIN_SAMPLE_SIZE}` };
  }
  if (proposedModifier < T.MIN_MODIFIER || proposedModifier > T.MAX_MODIFIER) {
    return { safe: false, reason: `Modifier ${proposedModifier} out of bounds [${T.MIN_MODIFIER}, ${T.MAX_MODIFIER}]` };
  }
  const current = _store.currentModifiers[signalKey] ?? T.NEUTRAL_MODIFIER;
  const jump    = Math.abs(proposedModifier - current);
  if (jump > G.MAX_JUMP + 1e-9) {   // +epsilon for float precision
    return { safe: false, reason: `Modifier jump ${jump.toFixed(3)} exceeds max ${G.MAX_JUMP} per step` };
  }
  return { safe: true, reason: null };
}

// ── Core actions ──────────────────────────────────────────────────────────────

/**
 * Propose a new modifier for a signal key.
 * Returns the proposal ID.
 * If requireApproval=false OR delta ≤ AUTO_APPROVE_MAX_DELTA, auto-approves.
 */
export function proposeModifier(
  signalKey:        string,
  proposedModifier: number,
  regimeContext:    RegimeKey,
  reason:           string,
  sampleSize:       number,
): { proposalId: string; autoApproved: boolean; safetyCheck: SafetyCheck } {
  const check = _validateProposal(signalKey, proposedModifier, sampleSize);
  const current = _store.currentModifiers[signalKey] ?? T.NEUTRAL_MODIFIER;
  const delta   = Math.abs(proposedModifier - current);

  const proposal: ModifierProposal = {
    id:               _nextPropId(),
    signalKey,
    currentModifier:  current,
    proposedModifier: Math.max(T.MIN_MODIFIER, Math.min(T.MAX_MODIFIER, proposedModifier)),
    delta,
    regimeContext,
    reason:           check.safe ? reason : (check.reason ?? reason),
    sampleSize,
    status:           check.safe ? 'pending' : 'rejected',
    createdSeq:       _store.nextSeq,
    resolvedSeq:      check.safe ? null : _store.nextSeq,
  };

  _store.proposals.push(proposal);

  // Record in ledger immediately (advisory entry, not yet applied)
  const ledgerEntry: LedgerEntry = {
    seq:              _store.nextSeq++,
    signalKey,
    previousModifier: current,
    newModifier:      proposal.proposedModifier,
    regimeContext,
    reason:           proposal.reason,
    sampleSize,
    mode:             _store.mode,
    applied:          false,
    proposalId:       proposal.id,
    recordedAt:       Date.now(),
  };
  _store.ledger.push(ledgerEntry);

  // Auto-approve conditions: governance says no approval needed, OR tiny delta
  const autoApprove = check.safe && (!_store.requireApproval || delta <= G.AUTO_APPROVE_MAX_DELTA);
  if (autoApprove) {
    _applyProposal(proposal.id);
  }

  return { proposalId: proposal.id, autoApproved: autoApprove, safetyCheck: check };
}

function _applyProposal(proposalId: string): void {
  const proposal = _store.proposals.find(p => p.id === proposalId);
  if (!proposal || proposal.status !== 'pending') return;

  // Re-validate at application time (defense-in-depth)
  const check = _validateProposal(proposal.signalKey, proposal.proposedModifier, proposal.sampleSize);
  if (!check.safe) {
    proposal.status = 'rejected';
    proposal.resolvedSeq = _store.nextSeq;
    return;
  }

  _store.currentModifiers[proposal.signalKey] = proposal.proposedModifier;
  proposal.status      = 'applied';
  proposal.resolvedSeq = _store.nextSeq;

  // Append applied entry to ledger
  _store.ledger.push({
    seq:              _store.nextSeq++,
    signalKey:        proposal.signalKey,
    previousModifier: proposal.currentModifier,
    newModifier:      proposal.proposedModifier,
    regimeContext:    proposal.regimeContext,
    reason:           proposal.reason,
    sampleSize:       proposal.sampleSize,
    mode:             _store.mode,
    applied:          true,
    proposalId:       proposal.id,
    recordedAt:       Date.now(),
  });

  // Check health after every application
  _computeHealth();
}

/** Operator approves a pending proposal */
export function approveProposal(proposalId: string): { success: boolean; reason: string } {
  const proposal = _store.proposals.find(p => p.id === proposalId);
  if (!proposal)                           return { success: false, reason: 'Proposal not found' };
  if (proposal.status !== 'pending')       return { success: false, reason: `Proposal is ${proposal.status}` };
  if (_store.mode === 'frozen')            return { success: false, reason: `Frozen: ${_store.frozenReason}` };

  if (_store.mode === 'active') {
    _applyProposal(proposalId);
    return { success: true, reason: 'Applied' };
  } else {
    // Advisory: approve but don't apply
    proposal.status      = 'approved';
    proposal.resolvedSeq = _store.nextSeq;
    _store.ledger.push({
      seq: _store.nextSeq++, signalKey: proposal.signalKey,
      previousModifier: proposal.currentModifier, newModifier: proposal.proposedModifier,
      regimeContext: proposal.regimeContext, reason: `Advisory: approved — ${proposal.reason}`,
      sampleSize: proposal.sampleSize, mode: _store.mode,
      applied: false, proposalId: proposal.id, recordedAt: Date.now(),
    });
    return { success: true, reason: 'Approved (advisory — not applied)' };
  }
}

/** Operator rejects a pending proposal */
export function rejectProposal(proposalId: string): { success: boolean } {
  const proposal = _store.proposals.find(p => p.id === proposalId);
  if (!proposal || proposal.status !== 'pending') return { success: false };
  proposal.status      = 'rejected';
  proposal.resolvedSeq = _store.nextSeq;
  _store.ledger.push({
    seq: _store.nextSeq++, signalKey: proposal.signalKey,
    previousModifier: proposal.currentModifier, newModifier: proposal.currentModifier,
    regimeContext: proposal.regimeContext, reason: `Rejected by operator`,
    sampleSize: proposal.sampleSize, mode: _store.mode,
    applied: false, proposalId: proposal.id, recordedAt: Date.now(),
  });
  return { success: true };
}

// ── Rollback engine ───────────────────────────────────────────────────────────

/**
 * Revert the last applied modifier for all signals.
 * Produces a compensating ledger entry — full audit trail is preserved.
 */
export function rollbackLast(): { rolledBack: string[]; count: number } {
  // Find the most recently applied entry per signal
  const lastApplied = new Map<string, LedgerEntry>();
  for (const e of _store.ledger) {
    if (e.applied) lastApplied.set(e.signalKey, e);
  }

  const rolledBack: string[] = [];
  for (const [signalKey, entry] of lastApplied.entries()) {
    _store.currentModifiers[signalKey] = entry.previousModifier;
    _store.ledger.push({
      seq:              _store.nextSeq++,
      signalKey,
      previousModifier: entry.newModifier,
      newModifier:      entry.previousModifier,
      regimeContext:    entry.regimeContext,
      reason:           `ROLLBACK_LAST: reverted from ${entry.newModifier} to ${entry.previousModifier}`,
      sampleSize:       entry.sampleSize,
      mode:             _store.mode,
      applied:          true,
      proposalId:       `ROLLBACK-${entry.proposalId}`,
      recordedAt:       Date.now(),
    });
    // Mark original proposal as rolled_back
    const prop = _store.proposals.find(p => p.id === entry.proposalId);
    if (prop) prop.status = 'rolled_back';
    rolledBack.push(signalKey);
  }

  return { rolledBack, count: rolledBack.length };
}

/**
 * Revert a specific signal's modifier to its previous value.
 */
export function rollbackSignal(signalKey: string): { success: boolean; revertedTo: number | null } {
  // Find the previous applied entry for this signal
  const applied = _store.ledger.filter(e => e.applied && e.signalKey === signalKey);
  if (applied.length < 1) return { success: false, revertedTo: null };

  const last = applied[applied.length - 1];
  _store.currentModifiers[signalKey] = last.previousModifier;
  _store.ledger.push({
    seq:              _store.nextSeq++,
    signalKey,
    previousModifier: last.newModifier,
    newModifier:      last.previousModifier,
    regimeContext:    last.regimeContext,
    reason:           `ROLLBACK_SIGNAL[${signalKey}]: reverted to ${last.previousModifier}`,
    sampleSize:       last.sampleSize,
    mode:             _store.mode,
    applied:          true,
    proposalId:       `ROLLBACK-${last.proposalId}`,
    recordedAt:       Date.now(),
  });
  return { success: true, revertedTo: last.previousModifier };
}

/**
 * Full system rollback — reset all modifiers to neutral.
 * System continues to operate; no freeze required.
 */
export function rollbackAll(): { count: number } {
  const signals = Object.keys(_store.currentModifiers);
  const nullRegime: RegimeKey = { volatility: 'unknown', gamma: 'unknown', thesis: 'unknown', trap: null };

  for (const sig of signals) {
    const prev = _store.currentModifiers[sig];
    _store.currentModifiers[sig] = T.NEUTRAL_MODIFIER;
    _store.ledger.push({
      seq:              _store.nextSeq++,
      signalKey:        sig,
      previousModifier: prev,
      newModifier:      T.NEUTRAL_MODIFIER,
      regimeContext:    nullRegime,
      reason:           'ROLLBACK_ALL: full system reset to neutral',
      sampleSize:       0,
      mode:             _store.mode,
      applied:          true,
      proposalId:       `ROLLBACK-ALL-${_store.nextSeq}`,
      recordedAt:       Date.now(),
    });
  }
  return { count: signals.length };
}

// ── Mode control ──────────────────────────────────────────────────────────────

export function setGovernanceMode(mode: GovernanceMode, reason?: string): void {
  _store.mode = mode;
  if (mode === 'frozen') {
    _store.frozenReason = reason ?? 'Manually frozen by operator';
  } else {
    _store.frozenReason = null;
  }
}

export function setRequireApproval(required: boolean): void {
  _store.requireApproval = required;
}

/** Freeze the system immediately. All adaptation halts. */
export function freeze(reason: string): void {
  setGovernanceMode('frozen', reason);
}

/** Unfreeze — returns to previous mode (advisory or active) */
export function unfreeze(targetMode: 'advisory' | 'active' = 'advisory'): void {
  _store.mode        = targetMode;
  _store.frozenReason = null;
}

// ── Read-only snapshot ────────────────────────────────────────────────────────

/** Returns a deep-cloned, serializable governance snapshot — never the live store. */
export function getGovernanceSnapshot(): GovernanceSnapshot {
  const health = _computeHealth();

  const proposals = _store.proposals;
  const stats = {
    totalProposals:  proposals.length,
    approvedCount:   proposals.filter(p => p.status === 'approved' || p.status === 'applied').length,
    rejectedCount:   proposals.filter(p => p.status === 'rejected').length,
    rolledBackCount: proposals.filter(p => p.status === 'rolled_back').length,
    appliedCount:    proposals.filter(p => p.status === 'applied').length,
  };

  return {
    mode:            _store.mode,
    frozenReason:    _store.frozenReason,
    requireApproval: _store.requireApproval,
    modifierCap:     { min: T.MIN_MODIFIER, max: T.MAX_MODIFIER },
    currentModifiers: { ..._store.currentModifiers },
    ledger:          [..._store.ledger],
    proposals:       [..._store.proposals],
    health,
    stats,
  };
}

/**
 * Restore governance state from a serialized snapshot.
 * Used for replay reconstruction — fully deterministic.
 * recordedAt values from the snapshot are preserved but are non-semantic.
 */
export function restoreGovernanceSnapshot(snap: GovernanceSnapshot): void {
  _store = {
    mode:             snap.mode,
    frozenReason:     snap.frozenReason,
    requireApproval:  snap.requireApproval,
    currentModifiers: { ...snap.currentModifiers },
    ledger:           [...snap.ledger],
    proposals:        [...snap.proposals],
    nextSeq:          snap.ledger.length > 0 ? Math.max(...snap.ledger.map(e => e.seq)) + 1 : 1,
    nextProposalId:   snap.proposals.length > 0 ? snap.proposals.length + 1 : 1,
  };
}

/** Reset to clean state — for testing or full system reset. */
export function resetGovernance(): void {
  _store = _makeStore();
}

/**
 * Bulk-propose all non-neutral modifiers from a learning profile.
 * Called after buildAdaptiveLearningProfile() to pipeline recommendations
 * into the governance system for approval/rejection.
 */
export function proposeFromLearningProfile(
  profile: AdaptiveLearningProfile,
  regimeContext: RegimeKey,
): { proposalId: string; autoApproved: boolean; safetyCheck: { safe: boolean; reason: string | null } }[] {
  const results = [];
  for (const wp of profile.weightProfiles) {
    if (Math.abs(wp.learnedModifier - T.NEUTRAL_MODIFIER) < 0.001) continue;
    results.push(proposeModifier(
      wp.layer, wp.learnedModifier, regimeContext, wp.reason, wp.sampleSize,
    ));
  }
  for (const cp of profile.calibrationProfiles) {
    if (cp.suggestedAdjustment === null) continue;
    if (Math.abs(cp.suggestedAdjustment - T.NEUTRAL_MODIFIER) < 0.001) continue;
    results.push(proposeModifier(
      `calib:${cp.signalKey}`, cp.suggestedAdjustment, regimeContext,
      cp.explanation, cp.sampleSize,
    ));
  }
  return results;
}

// ── Audit entry bridge ────────────────────────────────────────────────────────

/**
 * Record an AdaptiveAuditEntry (from adaptiveApplication.ts) into the governance ledger.
 * This ensures every applied modifier in the application layer is reflected here.
 */
export function recordAuditEntry(entry: AdaptiveAuditEntry): void {
  const current = _store.currentModifiers[entry.signalKey] ?? T.NEUTRAL_MODIFIER;
  _store.ledger.push({
    seq:              _store.nextSeq++,
    signalKey:        entry.signalKey,
    previousModifier: current,
    newModifier:      entry.modifier,
    regimeContext:    entry.regimeContext,
    reason:           `APPLIED: ${entry.reason} [${entry.appliedRule}]`,
    sampleSize:       entry.sampleSize,
    mode:             _store.mode,
    applied:          true,
    proposalId:       `AUDIT-${_store.nextSeq}`,
    recordedAt:       Date.now(),
  });
  _store.currentModifiers[entry.signalKey] = entry.modifier;
}
